<template>
	<nav class="page-navigation">
		<div class="page-navigation__list">
			<ul>
				<li
					v-for="(route, idx) in computedRoutes"
					:key="idx"
					class="page-navigation__list__item"
				>
					<router-link
						:to="getUrl(route.url)"
						:title="$t(route.label)"
						class="page-navigation__list__item__link"
					>
						<span :class="`bx bx-${route.icon}`" />
						<span class="page-navigation__list__item__link__label">{{
							$t(route.label)
						}}</span>
					</router-link>
				</li>
			</ul>
			<ul>
				<li class="page-navigation__list__item">
					<router-link
						:to="getUrl({ name: 'configurations' })"
						class="page-navigation__list__item__link"
					>
						<span class="bx bx-cog" />
						<span class="page-navigation__list__item__link__label">{{
							$t('routes.configurations')
						}}</span>
					</router-link>
				</li>
				<li v-if="!IS_OPS" class="page-navigation__list__item">
					<router-link
						:to="getUrl({ name: 'contracts' })"
						class="page-navigation__list__item__link"
					>
						<span class="bx bx-pen" />
						<span class="page-navigation__list__item__link__label">{{
							$t('sidebar.contracts')
						}}</span>
					</router-link>
				</li>
				<li v-if="!IS_OPS" class="page-navigation__list__item">
					<a :href="DOCS_URL" class="page-navigation__list__item__link" target="_blank">
						<span class="bx bx-help-circle" />
						<span class="page-navigation__list__item__link__label">{{
							$t('sidebar.help')
						}}</span>
					</a>
				</li>
			</ul>
		</div>
	</nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouteRecordRaw, useRoute } from 'vue-router';
import RoutesOps from '@apps/ops/src/data/routes.json';
import RoutesDashboard from '@apps/dashboard/src/data/routes.json';

interface TypePageNavigation {
	url: RouteRecordRaw;
	label: string;
	icon: string;
	env?: 'ci-latest' | 'sandbox' | 'production';
}

const props = defineProps<{
	isShowingHomepage: boolean;
}>();

const DOCS_URL = 'https://docs.payaut.com/docs/how/introduction/index.html';
const Routes = process.env.VUE_APP_TYPE === 'ops' ? RoutesOps : RoutesDashboard;
const navigationRoutes = Routes.routes as Array<TypePageNavigation>;
const route = useRoute();

const IS_OPS = process.env.VUE_APP_TYPE === 'ops';

const computedRoutes = computed((): TypePageNavigation[] => {
	if (IS_OPS) {
		return navigationRoutes.filter(
			(navRoute) => !navRoute.env || navRoute.env === process.env.VUE_APP_ENV
		);
	}

	return navigationRoutes.filter(
		(currRoute: TypePageNavigation) =>
			!(!props.isShowingHomepage && currRoute.url.name === 'homepage') &&
			(!currRoute.env || currRoute.env === process.env.VUE_APP_ENV)
	);
});

function getUrl(url: Partial<RouteRecordRaw>) {
	let newUrl = url;
	let query;
	if (route.name === url.name) {
		query = { query: route.query };
		newUrl = { ...url, ...query };
	} else {
		query = IS_OPS
			? {
					query: {
						merchantCode: route.query?.merchantCode,
						divisionCode: route.query?.divisionCode,
					},
			  }
			: {};
		newUrl = { ...url, ...query };
	}
	return newUrl;
}
</script>

<style lang="scss" scoped>
.page-navigation {
	height: 100%;

	&__list {
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		list-style: none;
		margin: 0;
		padding: $spacing-8 0 $spacing-8 1.5rem;

		&__item {
			margin-bottom: $spacing-4;

			&__link {
				color: $color-neutral-100;
				text-decoration: none;
				padding: 0.3rem 0;

				display: flex;
				align-items: center;
				width: 100%;
				white-space: nowrap;

				&__label {
					transition: font-weight 0.2s;
					overflow: hidden;
				}

				> svg {
					width: 24px;
					height: 24px;
					fill: white;
					margin-right: $spacing-4;
				}

				> .bx {
					font-size: 24px;
					fill: white;
					margin-right: $spacing-4;
				}

				&.router-link-active,
				&:hover {
					font-weight: 600;
					border-right: solid 0.3rem white;
					cursor: pointer;
				}
			}
		}
	}
}

.sidebar-open {
	.page-navigation {
		&__list__item {
			&__link {
				&__label {
					animation: sidebarOpen 0.55s forwards;
				}
			}
		}
	}
}

.sidebar-collapsed {
	.page-navigation {
		&__list__item {
			&__link {
				&__label {
					animation: sidebarCollapsed 0.55s forwards;
				}
			}
		}
	}
}

@keyframes sidebarOpen {
	0% {
		width: 0;
	}
	100% {
		width: 100%;
	}
}

@keyframes sidebarCollapsed {
	0% {
		width: 100%;
	}
	40% {
		width: 0;
	}
	100% {
		width: 0;
	}
}
</style>
